import { useMemo, useState } from 'react'
import { api } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Escalation policies and the approvals register.
 *
 * **Tiers are edited as rows, not as JSON.** The API carries `tiers` as a JSON string —
 * `[{"role":"ELECTRICAL","waitMinutes":15},…]` — and the old page asked an administrator to type that by hand.
 * These numbers are the acknowledgement deadline an engineer is judged against and the chain that fires when
 * it is missed; a misplaced bracket silently produces a policy that escalates to nobody. So the editor is a
 * table of role + wait, and the JSON is assembled here.
 *
 * The seed's own note explains why this screen matters more than it looks: with no policy row, nothing
 * escalates and every complaint honestly reports `slaState = NONE`. Wait times are plant policy, not a
 * technical default, so production ships with none — somebody has to enter them, and until now that meant
 * hand-writing JSON in a separate page behind a second login.
 *
 * There is no delete, matching the API: a policy that has been escalating is part of why an incident escalated
 * when it did, and removing the row rewrites that history. Deactivating stops it applying and keeps the record.
 */
export default function Escalation() {
  const policies = useApi('/escalation-policies')
  const pending = useApi('/approvals/pending')
  const all = useApi('/approvals')

  const [editing, setEditing] = useState(null)   // policy row, or 'new'
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const rows = useMemo(() => (Array.isArray(policies.data) ? policies.data : []), [policies.data])
  const pendingRows = useMemo(() => (Array.isArray(pending.data) ? pending.data : []), [pending.data])
  const allRows = useMemo(() => (Array.isArray(all.data) ? all.data : []), [all.data])

  async function run(fn, text) {
    setBusy(true); setNotice(null)
    try {
      await fn()
      policies.reload(); pending.reload(); all.reload()
      setNotice({ kind: 'success', text })
      setEditing(null)
    } catch (e) {
      setNotice({ kind: 'danger', text: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Escalation & approvals"
        subtitle="Who is told when a complaint goes unanswered, and who signed off which safety closure."
        actions={<Button onClick={() => setEditing('new')}>New policy</Button>}
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {rows.length === 0 && policies.data && (
        <Banner kind="warning">
          No escalation policy exists, so <strong>nothing escalates</strong> and every complaint reports no SLA
          state. Wait times are plant policy rather than a technical default, which is why production ships
          without them — add one below.
        </Banner>
      )}

      {policies.loading && <Loading rows={4} />}
      {policies.error && <ErrorState error={policies.error} onRetry={policies.reload} />}

      {policies.data && (
        <>
          <MetricRow>
            <Metric label="Policies" value={rows.length} />
            <Metric label="Active" value={rows.filter((p) => p.active !== false).length} kind="success" />
            <Metric
              label="Inactive"
              value={rows.filter((p) => p.active === false).length}
              hint="Kept for the audit trail"
            />
            <Metric
              label="Approvals pending"
              value={pendingRows.length}
              kind={pendingRows.length ? 'warning' : 'success'}
            />
          </MetricRow>

          <SectionHeader hint="Ordered L1 → L2 → L3; the wait is measured from the complaint being raised">
            Policies
          </SectionHeader>
          <Card>
            <Table
              keyOf={(p) => p.id}
              onRowClick={(p) => setEditing(p)}
              columns={[
                { key: 'name', header: 'Policy', render: (p) => <strong>{p.name}</strong> },
                { key: 'scope', header: 'Applies to', render: (p) => scopeLabel(p.scope) },
                { key: 'tiers', header: 'Chain', render: (p) => tierLabel(p.tiers) },
                {
                  key: 'active',
                  header: 'State',
                  render: (p) => (p.active === false
                    ? <StatusPill status="CLOSED" title="Inactive — does not escalate" />
                    : <StatusPill status="ACTIVE" />),
                },
              ]}
              rows={rows}
              empty={<p className="muted">No policies configured.</p>}
            />
          </Card>
        </>
      )}

      <SectionHeader hint="Waiting on a decision now">Approvals pending</SectionHeader>
      <Card accent={pendingRows.length ? 'warning' : undefined}>
        {pending.loading && <Loading rows={3} />}
        {pending.error && <ErrorState error={pending.error} onRetry={pending.reload} />}
        {pending.data && (
          <Table
            keyOf={(a) => a.id}
            columns={[
              { key: 'action', header: 'Decision', render: (a) => titleCase(a.action) },
              { key: 'entityType', header: 'On', render: (a) => titleCase(a.entityType) },
              { key: 'requestedByName', header: 'Requested by', render: (a) => a.requestedByName || <span className="muted">—</span> },
              { key: 'approverRole', header: 'Approver role', render: (a) => titleCase(a.approverRole) },
              {
                key: 'waiting',
                header: 'Waiting',
                numeric: true,
                // The server computes this, so the console cannot disagree with the escalation clock.
                render: (a) => (a.pendingMinutes == null ? '—' : fmtWait(a.pendingMinutes)),
              },
              { key: 'reason', header: 'Reason', render: (a) => a.reason || <span className="muted">—</span> },
              {
                key: 'act',
                header: '',
                render: (a) => (
                  <span className="cell-row">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => run(() => api(`/approvals/${a.id}/approve`, { method: 'POST', body: {} }), 'Approved.')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busy}
                      onClick={() => run(() => api(`/approvals/${a.id}/reject`, { method: 'POST', body: {} }), 'Rejected.')}
                    >
                      Reject
                    </Button>
                  </span>
                ),
              },
            ]}
            rows={pendingRows}
            empty={<p className="muted">Nothing waiting on a decision.</p>}
          />
        )}
      </Card>

      <SectionHeader hint="Every decision, newest first — this is the sign-off record">Approval history</SectionHeader>
      <Card>
        {all.loading && <Loading rows={3} />}
        {all.data && (
          <Table
            keyOf={(a) => a.id}
            columns={[
              { key: 'createdAt', header: 'Requested', render: (a) => fmtDate(a.createdAt) },
              { key: 'action', header: 'Decision', render: (a) => titleCase(a.action) },
              { key: 'entityType', header: 'On', render: (a) => titleCase(a.entityType) },
              { key: 'status', header: 'Outcome', render: (a) => <StatusPill status={a.status} /> },
              { key: 'decidedByName', header: 'Decided by', render: (a) => a.decidedByName || <span className="muted">—</span> },
              { key: 'decidedAt', header: 'Decided', render: (a) => fmtDate(a.decidedAt) },
            ]}
            rows={allRows}
            empty={<p className="muted">No approval requests recorded.</p>}
          />
        )}
      </Card>

      {editing && (
        <PolicyForm
          policy={editing === 'new' ? null : editing}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={(body) => {
            const isNew = editing === 'new'
            run(
              () => api(isNew ? '/escalation-policies' : `/escalation-policies/${editing.id}`, {
                method: isNew ? 'POST' : 'PATCH',
                body,
              }),
              isNew ? `Created "${body.name}".` : `"${body.name || editing.name}" updated.`,
            )
          }}
          onToggle={() => run(
            () => api(`/escalation-policies/${editing.id}`, { method: 'PATCH', body: { active: editing.active === false } }),
            editing.active === false ? 'Policy activated.' : 'Policy deactivated.',
          )}
        />
      )}
    </>
  )
}

/* ---- Policy editor ------------------------------------------------------- */

const ROLES = ['ELECTRICAL', 'MECHANICAL', 'SAFETY_OFFICER', 'ADMIN']
const PRIORITIES = ['', 'SAFETY', 'BREAKDOWN', 'MINOR']
const DISCIPLINES = ['', 'ELECTRICAL', 'MECHANICAL', 'EITHER']

function PolicyForm({ policy, busy, onClose, onSubmit, onToggle }) {
  const isNew = !policy
  const scope0 = parseJson(policy?.scope, {})
  const [name, setName] = useState(policy?.name || '')
  const [priority, setPriority] = useState(scope0.priority || '')
  const [discipline, setDiscipline] = useState(scope0.discipline || '')
  const [tiers, setTiers] = useState(() => {
    const t = parseJson(policy?.tiers, [])
    return Array.isArray(t) && t.length ? t : [{ role: 'MECHANICAL', waitMinutes: 15 }]
  })

  const setTier = (i, patch) => setTiers((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)))
  const addTier = () => setTiers((ts) => [...ts, { role: 'ADMIN', waitMinutes: (ts[ts.length - 1]?.waitMinutes || 15) * 2 }])
  const dropTier = (i) => setTiers((ts) => ts.filter((_, j) => j !== i))

  // Each tier must wait longer than the one before it, or L2 fires before L1 and the chain is meaningless.
  const outOfOrder = tiers.some((t, i) => i > 0 && Number(t.waitMinutes) <= Number(tiers[i - 1].waitMinutes))

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          const scope = {}
          if (priority) scope.priority = priority
          if (discipline) scope.discipline = discipline
          onSubmit({
            name: name.trim(),
            scope: JSON.stringify(scope),
            // Assembled here rather than typed: a misplaced bracket in this field produces a policy that
            // escalates to nobody, and nothing would say so.
            tiers: JSON.stringify(tiers.map((t) => ({ role: t.role, waitMinutes: Number(t.waitMinutes) }))),
          })
        }}
      >
        <header className="sheet-head">
          <h2>{isNew ? 'New escalation policy' : `Edit ${policy.name}`}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <Field label="Policy name" id="pn" hint="What this policy is for, e.g. 'Safety — 15 min to acknowledge'">
            <input id="pn" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </Field>

          <SectionHeader hint="Leave blank to apply to everything">Applies to</SectionHeader>
          <div className="grid-2">
            <Field label="Priority" id="pp">
              <select id="pp" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p || 'Any priority'}</option>)}
              </select>
            </Field>
            <Field label="Discipline" id="pd">
              <select id="pd" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
                {DISCIPLINES.map((d) => <option key={d} value={d}>{d || 'Any discipline'}</option>)}
              </select>
            </Field>
          </div>

          <SectionHeader hint="L1 is the acknowledgement deadline the engineer's queue displays">
            Escalation chain
          </SectionHeader>
          {tiers.map((t, i) => (
            <div className="row" key={i}>
              <Field label={`L${i + 1} role`} id={`tr-${i}`}>
                <select id={`tr-${i}`} value={t.role} onChange={(e) => setTier(i, { role: e.target.value })}>
                  {ROLES.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
                </select>
              </Field>
              <Field label="Wait (minutes)" id={`tw-${i}`} hint="From when the complaint was raised">
                <input
                  id={`tw-${i}`}
                  type="number"
                  min="1"
                  value={t.waitMinutes}
                  onChange={(e) => setTier(i, { waitMinutes: e.target.value })}
                  required
                />
              </Field>
              {tiers.length > 1 && (
                <Button type="button" variant="secondary" onClick={() => dropTier(i)}>Remove</Button>
              )}
            </div>
          ))}
          <Button type="button" variant="secondary" onClick={addTier}>Add a tier</Button>

          {outOfOrder && (
            <Banner kind="danger">
              Each tier must wait longer than the one before it. As written, a later tier fires at the same time
              as or before an earlier one, which means the chain does not escalate.
            </Banner>
          )}

          {!isNew && (
            <>
              <SectionHeader>{policy.active === false ? 'Activate' : 'Deactivate'}</SectionHeader>
              <p className="muted">
                There is no delete, deliberately: a policy that has been escalating is part of why an incident
                escalated when it did, and removing it rewrites that history. Deactivating stops it applying.
              </p>
              <Button type="button" variant={policy.active === false ? 'primary' : 'danger'} disabled={busy} onClick={onToggle}>
                {policy.active === false ? 'Activate policy' : 'Deactivate policy'}
              </Button>
            </>
          )}
        </div>

        <footer className="sheet-foot">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !name.trim() || outOfOrder}>
            {busy ? 'Saving…' : isNew ? 'Create policy' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}

/* ---- Rendering helpers --------------------------------------------------- */

function parseJson(s, fallback) {
  if (!s) return fallback
  if (typeof s !== 'string') return s
  try { return JSON.parse(s) } catch { return fallback }
}

function scopeLabel(scope) {
  const s = parseJson(scope, {})
  const parts = [s.priority, s.discipline].filter(Boolean).map(titleCase)
  return parts.length ? parts.join(' · ') : <span className="muted">Everything</span>
}

function tierLabel(tiers) {
  const t = parseJson(tiers, [])
  if (!Array.isArray(t) || !t.length) return <span className="muted">No chain — escalates to nobody</span>
  return t.map((x, i) => `L${i + 1} ${titleCase(x.role)} @ ${x.waitMinutes}m`).join(' → ')
}

const titleCase = (s) => (s ? s.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : '—')

function fmtWait(minutes) {
  if (minutes < 60) return `${Math.round(minutes)} min`
  if (minutes < 60 * 48) return `${(minutes / 60).toFixed(1)} h`
  return `${(minutes / 1440).toFixed(1)} d`
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
